import { Injectable, signal } from '@angular/core';

import { Observable, firstValueFrom, forkJoin, from, switchMap } from 'rxjs';
import { take, tap } from 'rxjs/operators';

import dayjs from 'dayjs';

import { AlertService } from '@core/services/alert/alert.service';
import { AppSettingsService } from '@core/services/app-settings/app-settings.service';
import { LoadingService } from '@core/services/loading/loading.service';
import { LoggerService } from '@core/services/logger/logger.service';
import { TaggedLogger } from '@core/services/logger/tagged-logger';
import { PushNotificationsPermissionError } from '@core/services/push-notifications/push-notifications.error';
import { PushNotificationsService } from '@core/services/push-notifications/push-notifications.service';
import { DateUtilService } from '@core/services/utils/date-util.service';
import { TypeUtilService } from '@core/services/utils/type-util.service';

import { NotificationSettingsApiService } from '@pages/other/profile/notifications';

import { BaseProfileCommand } from '@shared/commands/base-profile.command';
import { IProfileCommandContext } from '@shared/models/profile-command.model';
import { CommandAvailabilityService } from '@shared/services/command-availability.service';

@Injectable()
export class EnableNotificationCommand extends BaseProfileCommand {
  private _dynamicSubtitle?: string;
  private readonly loggerService: TaggedLogger;

  readonly type = 'enableNotification';
  readonly title = 'Push-уведомления';
  readonly icon = 'notification';
  readonly checkbox = 'checked' as const;
  readonly group = 'push';
  readonly order = 3;
  hidden = false;
  readonly settingsNames = undefined;
  readonly subtitle = signal<string>('');
  readonly checkboxChecked = signal(false);
  readonly checkboxDisabled = signal(false);

  constructor(
    private readonly pushNotificationsService: PushNotificationsService,
    private readonly notificationSettingsApiService: NotificationSettingsApiService,
    private readonly loadingService: LoadingService,
    private readonly alertService: AlertService,
    loggerService: LoggerService,
    commandAvailabilityService?: CommandAvailabilityService,
    appSettingsService?: AppSettingsService
  ) {
    super(commandAvailabilityService, appSettingsService);
    this.loggerService = loggerService.createLogger('[EnableNotificationCommand]');
    this.pushNotificationsService.pushAvailableOnCurrentPlatform().then((value) => {
      this.hidden = !value;
    });
  }

  public async execute(_context: IProfileCommandContext): Promise<void> {
    const previousState = this.checkboxChecked();
    const newState = !previousState;
    const errorMessage = `Не удалось ${newState ? 'включить' : 'отключить'} PUSH-уведомления. Попробуйте ещё раз.`;
    let shouldShowLoader = false;

    this.updateCheckboxState(previousState, true);
    this.loggerService.info('Изменение состояния PUSH-уведомлений', { enabled: newState });

    try {
      shouldShowLoader = await this.pushNotificationsService.pushAvailableOnCurrentPlatform();

      if (shouldShowLoader) {
        this.loadingService.showLoader(true);
      }

      const result = await firstValueFrom(this.createNotificationToggleStream(newState));

      if (!result) {
        this.loggerService.warn('Состояние PUSH-уведомлений не изменено', { enabled: newState });
        void this.alertService.showErrorAlert(errorMessage);
        return;
      }

      await this.setSetting(newState);
      this.loggerService.info('Локальная настройка PUSH-уведомлений сохранена', { enabled: newState });

      if (newState) {
        const currentDate = dayjs().format('DD.MM.YYYY HH:mm ') + '+0300';
        this.setEnablePushDate(currentDate);
      } else {
        this._dynamicSubtitle = '';
        this.subtitle.set('');
      }

      this.updateCheckboxState(newState);
      this.loggerService.info('Состояние PUSH-уведомлений изменено', { enabled: newState });
    } catch (error) {
      this.loggerService.error('Ошибка изменения состояния PUSH-уведомлений', error, { enabled: newState });

      if (error instanceof PushNotificationsPermissionError) {
        this.pushNotificationsService.showPermissionAlert();
      } else {
        void this.alertService.showErrorAlert(errorMessage);
      }
    } finally {
      this.updateCheckboxState(this.checkboxChecked(), false);

      if (shouldShowLoader) {
        this.loadingService.showLoader(false);
      }
    }
  }

  /**
   * Переключает состояние чекбокса (вызывается при клике на чекбокс)
   */
  public async toggle(): Promise<void> {
    await this.execute({ data: {} });
  }

  /**
   * Проверяет дополнительную доступность - поддержка устройством
   */
  protected checkAdditionalAvailability(_context: IProfileCommandContext): Observable<boolean> {
    return from(this.pushNotificationsService.pushAvailableOnCurrentPlatform()).pipe(
      tap(() => {
        this.initialSubTitleWithToggleState();
      })
    );
  }

  /**
   * Создает реактивный поток для переключения уведомлений
   */
  private createNotificationToggleStream(newState: boolean): Observable<boolean> {
    if (newState) {
      // Включение уведомлений
      return this.pushNotificationsService.reRegistrationDevice().pipe(take(1));
    } else {
      // Отключение уведомлений
      return this.pushNotificationsService.unsubscribeDevice().pipe(take(1));
    }
  }

  private setEnablePushDate(date: string) {
    const parsedDate = dayjs(date, 'DD.MM.YYYY HH:mm');
    const tz = DateUtilService.getTimezoneName(date, true);

    this._dynamicSubtitle = `Подключено: ${parsedDate.format('DD.MM.YYYY')} <br/> в ${parsedDate.format('HH:mm')} (${tz})`;
    this.subtitle.set(this._dynamicSubtitle);
  }

  private initialSubTitleWithToggleState() {
    forkJoin([
      this.pushNotificationsService.notificationsIsEnabled(),
      this.notificationSettingsApiService.getNotificationsInfo(true)
    ]).subscribe({
      next: ([notificationEnabled, notificationsInfo]) => {
        const enabled = !TypeUtilService.isTruthy(notificationsInfo.push.enabled) ? false : notificationEnabled;
        this.updateCheckboxState(enabled);
        void this.setSetting(enabled);
        if (enabled && notificationsInfo.push.enableDate) {
          this.setEnablePushDate(notificationsInfo.push.enableDate);
        } else {
          this._dynamicSubtitle = '';
          this.subtitle.set('');
        }
      }
    });
  }

  /**
   * Устанавливает информацию об активном устройстве
   */
  private setRegisteredDeviceInfo(deviceInfo: { registered: boolean; deviceName?: string; platform?: string }): void {
    this._dynamicSubtitle = `Активное устройство: ${deviceInfo.deviceName}`;
    this.subtitle.set(this._dynamicSubtitle);
  }
}
